import {
  ChangeDetectorRef,
  DestroyRef,
  Directive,
  inject,
  Input,
  TemplateRef,
  ViewContainerRef,
} from '@angular/core';
import { select, Store } from '@ngrx/store';
import { filter, lastValueFrom, Observable, take } from 'rxjs';
import { PermissionModels } from '@share-models';
import { AppState } from '../store/app.state';
import { organizationFeature } from '../store/state/organization/organization.feature';
import { userFeature } from '../store/state/user/user.feature';
import { permissionsFeature } from '../store/state/permissions/permissions.feature';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Directive({
  selector: '[rbacOrgAllow]',
  standalone: true,
})
export class RbacOrgAllowDirective {
  private templateRef = inject<TemplateRef<unknown>>(TemplateRef);
  private viewContainer = inject(ViewContainerRef);
  private store$ = inject<Store<AppState>>(Store);
  private cdr = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);

  public selectIsDeveloper$: Observable<boolean> = this.store$.pipe(
    select(userFeature.selectIsDeveloper),
  );

  public allowedPermission: PermissionModels.Permissions[] = [];
  private isDefaultOrganization = false;
  private isDeveloper = false;

  constructor() {
    this.store$
      .pipe(
        select(organizationFeature.selectIsDefaultOrganization),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((isDefaultOrganization) => {
        this.isDefaultOrganization = isDefaultOrganization;
        void this.showIfUserAuthorized();
      });

    this.selectIsDeveloper$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((isDeveloper) => {
        this.isDeveloper = isDeveloper;
        void this.showIfUserAuthorized();
      });
  }

  @Input()
  public set rbacOrgAllow(permissions: PermissionModels.Permissions[]) {
    this.allowedPermission = permissions ?? [];
    void this.showIfUserAuthorized();
  }

  private async showIfUserAuthorized(): Promise<void> {
    this.viewContainer.clear();
    if (!this.allowedPermission || this.allowedPermission.length === 0) {
      return;
    }

    const userPermissions$ = this.store$.pipe(
      select(permissionsFeature.selectPermissionsState),
      filter(this.skipEmptyPermissions.bind(this)),
      take(1),
    );
    const userPermissions = await lastValueFrom(userPermissions$);

    const isAuthorized = PermissionModels.isAccessAllowed({
      allowedPermissions: this.allowedPermission,
      userPermissions: userPermissions,
      ids: [],
      isDeveloper: this.isDeveloper,
    });

    if (isAuthorized || this.isDefaultOrganization) {
      this.viewContainer.createEmbeddedView(this.templateRef);
      this.cdr.detectChanges(); // Trigger change detection
    }
  }

  private skipEmptyPermissions(
    permissionsState: PermissionModels.UserPermissionsState,
  ): boolean {
    return (
      permissionsState.permissions.length > 0 ||
      permissionsState.entityIds.length > 0
    );
  }
}
